import {useEffect, useState} from 'react'
import PropTypes from 'prop-types'

import {SAVE_STATUS} from './hooks/useLifeStoryDraft'

// "Saved just now" is only true for so long; after this it settles to "Saved".
export const JUST_NOW_WINDOW_MS = 30000

const formatTime = timestamp => new Date(timestamp).toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})

/**
 * The prompt screen's save status (TK-3179 AC 3), shown on the answer card
 * opposite the mic. Every state reflects what the server has confirmed:
 * "Saving…" only while a request is in flight, "Saved" only once one has
 * landed, and a failure only after the retries ran out — never an early
 * reassurance. Before anything has been saved it reads "Saves automatically".
 */
const SaveIndicator = ({status, lastSavedAt, retry}) => {
  const [justNow, setJustNow] = useState(false)

  // Measured from the save's timestamp, not from mount: the prompt screen
  // remounts per question, and a save from minutes ago must not read as
  // "just now" on the next screen.
  useEffect(() => {
    const remaining = status === SAVE_STATUS.SAVED && lastSavedAt ?
      JUST_NOW_WINDOW_MS - (Date.now() - lastSavedAt) :
      0

    setJustNow(remaining > 0)

    const timer = remaining > 0 ? setTimeout(() => setJustNow(false), remaining) : null

    return () => clearTimeout(timer)
  }, [status, lastSavedAt])

  const content = () => {
    switch (status) {
      case SAVE_STATUS.SAVING:
        return <span className='ls-save-indicator-saving'><span className='ls-spin' /> Saving&hellip;</span>
      case SAVE_STATUS.SAVED:
        return justNow ? 'Saved just now' : 'Saved'
      case SAVE_STATUS.ERROR:
        return (
          <span className='ls-save-indicator-error'>
            We couldn&rsquo;t save. Check your connection.
            {' '}
            <button className='ls-save-indicator-retry' onClick={retry}>Retry</button>
          </span>
        )
      case SAVE_STATUS.UNAUTHORIZED:
        return (
          <span className='ls-save-indicator-error'>
            You&rsquo;ve been signed out.
            {lastSavedAt ? ` Last saved at ${formatTime(lastSavedAt)}.` : ' Recent changes aren\u2019t saved.'}
          </span>
        )
      default:
        // Nothing has been saved yet this visit: say what will happen, so the
        // writer knows they don't have to do anything.
        return 'Saves automatically'
    }
  }

  const message = content()

  return (
    <span aria-live='polite' className='ls-save-indicator' role='status'>
      {message}
    </span>
  )
}

SaveIndicator.propTypes = {
  lastSavedAt: PropTypes.number,
  retry: PropTypes.func,
  status: PropTypes.string
}

export default SaveIndicator
